import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { AdminHeader, Btn, Card } from "../../components/admin/Fields";
import ImageUpload, { resolveImage } from "../../components/admin/ImageUpload";
import { refreshSiteSettings } from "../../lib/settings";
import { DEFAULT_FOOTER_LOGO_HEIGHT, DEFAULT_HEADER_LOGO_HEIGHT } from "../../lib/logo";

const HeightSlider = ({ label, value, onChange, fallback, testId }) => {
  const v = Number(value) > 0 ? Number(value) : fallback;
  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-2">
        <label className="text-[0.7rem] tracking-[0.28em] uppercase text-brand-navy font-medium">{label}</label>
        <span className="text-xs text-brand-medium tabular-nums" data-testid={`${testId}-value`}>{v} px</span>
      </div>
      <input
        type="range"
        min={24}
        max={140}
        value={v}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[#22346B]"
        data-testid={testId}
      />
    </div>
  );
};

const Preview = ({ url, dark, height, label, testId }) => {
  const src = resolveImage(url || "");
  return (
    <div>
      <div className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-medium mb-2">{label}</div>
      <div
        className={`flex items-center px-6 h-28 border ${dark ? "bg-brand-navy border-brand-navy" : "bg-white border-black/10"}`}
        data-testid={testId}
      >
        {src ? (
          <img src={src} alt="" style={{ height: Math.min(height, 96), width: "auto" }} className="object-contain" />
        ) : (
          <span className={`text-xs ${dark ? "text-white/50" : "text-brand-medium"}`}>Logo yüklenmedi</span>
        )}
      </div>
    </div>
  );
};

const LogosPage = () => {
  const [s, setS] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data || {})); }, []);

  const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", s);
      await refreshSiteSettings();
      toast.success("Logolar kaydedildi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setBusy(false);
    }
  };

  const headerH = Number(s.header_logo_height) > 0 ? Number(s.header_logo_height) : DEFAULT_HEADER_LOGO_HEIGHT;
  const footerH = Number(s.footer_logo_height) > 0 ? Number(s.footer_logo_height) : DEFAULT_FOOTER_LOGO_HEIGHT;

  return (
    <div data-testid="admin-logos-page">
      <AdminHeader
        eyebrow="Marka"
        title="Logolar"
        action={<Btn onClick={save} disabled={busy} testId="logos-save">Kaydet</Btn>}
      />

      <Card title="Header Logosu">
        <p className="text-sm text-brand-medium mb-6">
          Header tüm sayfalarda görünür. Ana sayfada header en üstte şeffaftır ve koyu görselin üzerinde durur —
          bu yüzden iki varyant yükleyin: beyaz zemin için koyu logo, koyu zemin için açık/beyaz logo.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <ImageUpload
              label="Header — Açık Zemin (beyaz header için koyu logo)"
              value={s.header_logo_url}
              onChange={(v) => set("header_logo_url", v)}
              testId="logos-header-light-bg"
            />
            <p className="text-xs text-brand-medium mt-2">Şeffaf PNG veya SVG. En az 200 px yükseklik önerilir.</p>
          </div>
          <div>
            <ImageUpload
              label="Header — Koyu Zemin (hero üzerinde beyaz logo)"
              value={s.header_logo_light_url}
              onChange={(v) => set("header_logo_light_url", v)}
              testId="logos-header-dark-bg"
              darkPreview
            />
            <p className="text-xs text-brand-medium mt-2">Boş bırakırsanız açık zemin logosu otomatik beyazlatılır.</p>
          </div>
        </div>
        <HeightSlider
          label="Header Logo Yüksekliği"
          value={s.header_logo_height}
          fallback={DEFAULT_HEADER_LOGO_HEIGHT}
          onChange={(v) => set("header_logo_height", v)}
          testId="logos-header-height"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
          <Preview label="Beyaz header önizleme" url={s.header_logo_url} height={headerH} testId="logos-preview-header-light" />
          <Preview label="Koyu header önizleme" url={s.header_logo_light_url || s.header_logo_url} height={headerH} dark testId="logos-preview-header-dark" />
        </div>
      </Card>

      <Card title="Footer Logosu">
        <p className="text-sm text-brand-medium mb-6">
          Footer lacivert zemin üzerindedir; beyaz veya açık renkli bir logo yükleyin. Boş bırakırsanız header
          logosu kullanılır ve gerekirse otomatik beyazlatılır.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <ImageUpload
              label="Footer Logosu"
              value={s.footer_logo_url}
              onChange={(v) => set("footer_logo_url", v)}
              testId="logos-footer"
              darkPreview
            />
          </div>
          <Preview label="Footer önizleme" url={s.footer_logo_url || s.header_logo_light_url || s.header_logo_url} height={footerH} dark testId="logos-preview-footer" />
        </div>
        <HeightSlider
          label="Footer Logo Yüksekliği"
          value={s.footer_logo_height}
          fallback={DEFAULT_FOOTER_LOGO_HEIGHT}
          onChange={(v) => set("footer_logo_height", v)}
          testId="logos-footer-height"
        />
      </Card>
    </div>
  );
};

export default LogosPage;
